import React from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../i18n/languages';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';

const languages = supportedLanguages.map((lang) => ({
  code: lang.code,
  name: lang.label,
  flag: lang.flag
}));

export function LanguageSelector({ variant = 'default', showLabel = true }) {
  const { i18n, t } = useTranslation();

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Optionally save to backend
    localStorage.setItem('i18nextLng', langCode);
  };

  if (variant === 'minimal') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 px-2" data-testid="language-selector-trigger">
            <span className="text-lg">{currentLanguage.flag}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={i18n.language === lang.code ? 'bg-muted' : ''}
              data-testid={`language-option-${lang.code}`}
            >
              <span className="text-lg mr-2">{lang.flag}</span>
              {lang.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="language-selector-trigger">
          <Globe className="h-4 w-4" />
          {showLabel && (
            <>
              <span className="text-lg">{currentLanguage.flag}</span>
              <span className="hidden sm:inline">{currentLanguage.name}</span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`cursor-pointer ${i18n.language === lang.code ? 'bg-muted' : ''}`}
            data-testid={`language-option-${lang.code}`}
          >
            <span className="text-lg mr-2">{lang.flag}</span>
            {lang.name}
            {i18n.language === lang.code && (
              <span className="ml-auto text-teal-600">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
